import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { VStack, Section } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import Chip from 'tio-ui/components/chip';
import Button from 'tio-ui/components/button';
import PageHero from 'tio-ui/components/page-hero';
import SectionHeader from 'tio-ui/components/section-header';
import Drawer from 'tio-ui/components/drawer';
import Popover from 'tio-ui/components/popover';
import Select from 'tio-ui/components/select';
import type { SelectOption } from 'tio-ui/components/select';
import { PhArrowLeft, PhSidebar, PhDotsThreeVertical, PhFlask } from 'ember-phosphor-icons';

const ACTION_OPTIONS: SelectOption[] = [
  { key: 'approve', label: 'Approve' },
  { key: 'reject', label: 'Reject' },
  { key: 'escalate', label: 'Escalate' },
  { key: 'reassign', label: 'Reassign' },
  { key: 'archive', label: 'Archive' },
];

export default class DrawerDetailPage extends Component {
  @tracked isOpen = false;
  @tracked placement: 'left' | 'right' | 'top' | 'bottom' = 'right';
  @tracked size: 'sm' | 'md' | 'lg' | 'xl' | 'full' = 'md';

  // Popover+Select drawer demo
  @tracked isPopoverDrawerOpen = false;
  @tracked selectedAction: string | null = null;

  openPopoverDrawer = () => {
    this.isPopoverDrawerOpen = true;
  };

  closePopoverDrawer = () => {
    this.isPopoverDrawerOpen = false;
  };

  setSelectedAction = (key: string | null) => {
    this.selectedAction = key;
  };

  openDrawer = (p: 'left' | 'right' | 'top' | 'bottom', s: 'sm' | 'md' | 'lg' | 'xl' | 'full') => {
    this.placement = p;
    this.size = s;
    this.isOpen = true;
  };

  closeDrawer = () => {
    this.isOpen = false;
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0">
      <div class="mb-4">
        <LinkTo @route="authenticated.reimagined.components" class="tio-demo-link">
          <PhArrowLeft @size={{18}} />
          Back to Components
        </LinkTo>
      </div>

      <PageHero
        @title="Drawer Component"
        @subtitle="Slide-out panel overlay with backdrop, keyboard (Escape) support, close-on-outside-click, and named blocks."
        @icon={{PhSidebar}}
        @gradient="cool"
        @decoration="dots"
      >
        <:actions>
          <Chip @intent="primary">4 Placements</Chip>
          <Chip @intent="success">5 Sizes</Chip>
        </:actions>
      </PageHero>

      <div class="space-y-8">
        <Section>
          <:header>
            <div class="tio-row-tight">
              <PhSidebar @size={{20}} class="tio-fg-brand" />
              <span>Drawer</span>
              <Chip @size="sm" @intent="primary">Overlay</Chip>
            </div>
          </:header>
          <:body>
            <Text @variant="muted" class="mb-4">
              Slide-out panel overlay with backdrop, keyboard (Escape) support,
              close-on-outside-click, and named blocks for header, body, and footer. 4 placements, 5
              sizes. Portaled to document.body for z-index safety.
            </Text>

            <div class="tio-surface-sunken p-6 mb-4 space-y-6">
              <div>
                <Text @size="sm" @weight="semibold" class="tio-fg-secondary mb-3">Right Placement:</Text>
                <div class="tio-wrap-tight">
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @size="sm"
                    {{on "click" (fn this.openDrawer "right" "sm")}}
                  >Right (sm)</Button>
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @size="sm"
                    {{on "click" (fn this.openDrawer "right" "md")}}
                  >Right (md)</Button>
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @size="sm"
                    {{on "click" (fn this.openDrawer "right" "lg")}}
                  >Right (lg)</Button>
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @size="sm"
                    {{on "click" (fn this.openDrawer "right" "xl")}}
                  >Right (xl)</Button>
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @size="sm"
                    {{on "click" (fn this.openDrawer "right" "full")}}
                  >Right (full)</Button>
                </div>
              </div>

              <div>
                <Text @size="sm" @weight="semibold" class="tio-fg-secondary mb-3">Left Placement:</Text>
                <div class="tio-wrap-tight">
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @size="sm"
                    {{on "click" (fn this.openDrawer "left" "sm")}}
                  >Left (sm)</Button>
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @size="sm"
                    {{on "click" (fn this.openDrawer "left" "md")}}
                  >Left (md)</Button>
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @size="sm"
                    {{on "click" (fn this.openDrawer "left" "lg")}}
                  >Left (lg)</Button>
                </div>
              </div>

              <div>
                <Text @size="sm" @weight="semibold" class="tio-fg-secondary mb-3">Top / Bottom:</Text>
                <div class="tio-wrap-tight">
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @size="sm"
                    {{on "click" (fn this.openDrawer "top" "sm")}}
                  >Top (sm)</Button>
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @size="sm"
                    {{on "click" (fn this.openDrawer "top" "md")}}
                  >Top (md)</Button>
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @size="sm"
                    {{on "click" (fn this.openDrawer "bottom" "md")}}
                  >Bottom (md)</Button>
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @size="sm"
                    {{on "click" (fn this.openDrawer "bottom" "lg")}}
                  >Bottom (lg)</Button>
                </div>
              </div>
            </div>

            <Drawer
              @isOpen={{this.isOpen}}
              @onClose={{this.closeDrawer}}
              @placement={{this.placement}}
              @size={{this.size}}
            >
              <:header>
                <Heading @level={{3}}>Sample Drawer</Heading>
              </:header>
              <:body>
                <div class="space-y-4">
                  <Text>
                    Slides in from the
                    <strong>{{this.placement}}</strong>
                    at size
                    <strong>{{this.size}}</strong>.
                  </Text>
                  <Text @variant="muted">Press Escape, click backdrop, or use the close button.</Text>
                </div>
              </:body>
              <:footer>
                <div class="tio-flex-end">
                  <Button @intent="primary" {{on "click" this.closeDrawer}}>Close</Button>
                </div>
              </:footer>
            </Drawer>

            <details class="border rounded-lg">
              <summary class="tio-demo-disclosure-summary">
                Code Examples
              </summary>
              <pre class="tio-demo-code-block"><code>&lt;Drawer
                  @isOpen=&#123;&#123;this.isOpen&#125;&#125;
                  @onClose=&#123;&#123;this.close&#125;&#125; @placement="right" @size="md" &gt;
                  &lt;:header&gt;&lt;Heading
                  @level=&#123;&#123;3&#125;&#125;&gt;Title&lt;/Heading&gt;&lt;/:header&gt;
                  &lt;:body&gt;Content&lt;/:body&gt; &lt;:footer&gt;Footer&lt;/:footer&gt;
                  &lt;/Drawer&gt;</code></pre>
            </details>
          </:body>
        </Section>

        {{! ================================================================
              Section 2: Drawer with Popover + Select
              ================================================================ }}
        <Section>
          <:header>
            <SectionHeader
              @icon={{PhFlask}}
              @title="Drawer with Popover & Select"
              @subtitle="Three-dot menu opens a popover with an inline select — proves all three layers (drawer → popover → select dropdown) portal correctly."
              @iconClass="text-teal-500"
            />
          </:header>
          <:body>
            <Text @variant="muted" class="mb-4">
              Opens a drawer whose header contains a kebab (⋮) button. Clicking it reveals a popover
              with a Select dropdown inside. All three overlays coexist without z-index conflicts
              because each portals to the document body.
            </Text>

            <Button @intent="primary" {{on "click" this.openPopoverDrawer}}>
              Open Drawer with Popover
            </Button>
          </:body>
        </Section>
      </div>

      {{! Popover+Select Drawer }}
      <Drawer
        @isOpen={{this.isPopoverDrawerOpen}}
        @onClose={{this.closePopoverDrawer}}
        @placement="right"
        @size="md"
      >
        <:header>
          <Heading @level={{3}}>Request Details</Heading>
        </:header>
        <:body>
          <div class="space-y-4">
            <div class="tio-row-between tio-surface-sunken px-4 py-2">
              <Text @size="sm" @weight="medium" class="tio-fg-secondary">Actions</Text>
              <Popover @placement="bottom-end" @showCloseButton={{true}}>
                <:trigger as |t|>
                  <button
                    type="button"
                    {{t.register}}
                    {{on "click" t.toggle}}
                    class="p-1.5 rounded-lg hover:tio-bg-sunken-hover transition-colors tio-fg-muted hover:tio-fg-secondary dark:hover:text-gray-200"
                    aria-label="More actions"
                  >
                    <PhDotsThreeVertical @size={{20}} />
                  </button>
                </:trigger>
                <:header>
                  <span class="tio-text-sm-semibold">Quick Actions</span>
                </:header>
                <:body>
                  <div class="space-y-3 min-w-[220px]">
                    <Select
                      @label="Action"
                      @items={{ACTION_OPTIONS}}
                      @selected={{this.selectedAction}}
                      @onChange={{this.setSelectedAction}}
                      @placeholder="Choose an action…"
                      @size="sm"
                    />
                    <div class="text-xs tio-fg-muted font-mono">
                      Selected:
                      {{if this.selectedAction this.selectedAction "none"}}
                    </div>
                  </div>
                </:body>
                <:footer>
                  <div class="tio-flex-end">
                    <Button @intent="primary" @size="sm">Apply</Button>
                  </div>
                </:footer>
              </Popover>
            </div>

            <Text>
              This drawer body contains a toolbar with a three-dot menu button that opens a popover.
              Inside that popover is a Select component. All three layers stack correctly.
            </Text>
            <Text @variant="muted">
              Try opening the popover, then the select dropdown — the dropdown portals above both
              the popover and drawer overlays.
            </Text>
          </div>
        </:body>
        <:footer>
          <div class="tio-flex-end">
            <Button @intent="primary" {{on "click" this.closePopoverDrawer}}>Close</Button>
          </div>
        </:footer>
      </Drawer>
    </VStack>
  </template>
}
